import { bookingTime } from '../../../../backend/shared/utils/agreed-times';
import { resolveBookingPrice, isIlocaImported } from '../../../../backend/shared/utils/booking-price';
/**
 * Booking Adapter
 * 
 * Transforms between OpenAPI-compliant nested structure (from API)
 * and flat structure expected by existing frontend components
 * 
 * This is a temporary adapter until all frontend components are updated
 * to use the correct nested structure
 */

import { guestNameValue } from './bookingGuestLabel';
import { Booking } from '../types/booking';
import { bookingRoomLabel, isBotSource, isUnassignedRoom } from './bookingRoomLabel';

/**
 * OpenAPI-compliant booking structure from API
 */
export interface ApiBooking {
  agreedTimes?: Booking['agreedTimes'];
  guestConfirmedAt?: unknown;
  botBooking?: Booking['botBooking'];
  bookingReference: string;
  source: string;
  guest?: {
    fullName?: string;
    email?: string | null;
    primaryPhone?: string | null;
  };
  // Flat guest fields (alternative to nested guest object)
  guestName?: string;
  guestEmail?: string;
  guestPhone?: string;
  primaryPhone?: string;
  email?: string;
  counts?: { totalGuests?: number; adults?: number; children?: number };
  numberOfGuests?: number;
  stay?: {
    checkIn?: string;
    checkOut?: string;
    nights?: number;
    requestedCheckInTime?: string;
    requestedCheckOutTime?: string;
  };
  room?: {
    roomName?: string;
    roomNumber?: string;
    roomType?: string;
  };
  // Flat room fields (alternative to nested room object)
  roomNumber?: string;
  roomName?: string;
  roomType?: string;
  status: string;
  references?: string[];
  referenceMap?: Record<string, any>;
  specialRequests?: string | null;
  totalAmount?: number | null;
  currencyCode?: string;
  earlyCheckinApproved?: boolean;
  lateCheckoutApproved?: boolean;
  invoiceSent?: boolean;
  createdAt?: string;
  updatedAt?: string;
  id?: string;
}

/**
 * Convert timestamp objects to ISO strings
 * Handles Firestore Timestamps, Date objects, strings, and numbers
 */
function convertTimestampToString(timestamp: any): string | undefined {
  if (!timestamp) return undefined;
  
  // Handle Firestore Timestamp objects (both formats)
  if (timestamp && typeof timestamp === 'object') {
    // Firestore Timestamp with _seconds or seconds property
    if ('_seconds' in timestamp || 'seconds' in timestamp || (timestamp.constructor && timestamp.constructor.name === 'Timestamp')) {
      try {
        // Try toDate() method first (Firestore SDK v9+)
        if ('toDate' in timestamp && typeof timestamp.toDate === 'function') {
          const date = timestamp.toDate();
          return isNaN(date.getTime()) ? undefined : date.toISOString();
        }
        // Fallback to _seconds/seconds (Firestore SDK v8 or plain objects)
        const seconds = timestamp._seconds || timestamp.seconds;
        if (seconds) {
          const nanoseconds = timestamp._nanoseconds || timestamp.nanoseconds || 0;
          const date = new Date(seconds * 1000 + nanoseconds / 1000000);
          return isNaN(date.getTime()) ? undefined : date.toISOString();
        }
      } catch (error) {
        console.warn('[Date Error] Failed to convert Firestore Timestamp:', error, timestamp);
        return undefined;
      }
    }
    // Handle Date objects
    if (timestamp instanceof Date) {
      return isNaN(timestamp.getTime()) ? undefined : timestamp.toISOString();
    }
    // If it's a plain object (not a Date or Timestamp), don't convert to "[object Object]"
    // This prevents the error we're seeing
    if (timestamp.constructor === Object || !timestamp.constructor) {
      console.warn('[Date Error] Received plain object instead of timestamp:', timestamp);
      return undefined;
    }
  }
  
  // Handle string dates
  if (typeof timestamp === 'string') {
    // If it's already an ISO string, return it
    if (timestamp.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
      return timestamp;
    }
    // Try to parse it
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? undefined : date.toISOString();
  }
  
  // Handle numbers (unix timestamps)
  if (typeof timestamp === 'number') {
    // If it's in seconds (less than year 2000 in milliseconds), multiply by 1000
    const ms = timestamp < 946684800000 ? timestamp * 1000 : timestamp;
    const date = new Date(ms);
    return isNaN(date.getTime()) ? undefined : date.toISOString();
  }
  
  // Don't convert unknown types to "[object Object]"
  console.warn('[Date Error] Invalid timestamp type:', typeof timestamp, timestamp);
  return undefined;
}

/**
 * Transform API booking (nested) to frontend booking (flat)
 */
export function transformApiToFrontend(apiBooking: ApiBooking): Booking {
  // Extract requested check-in/out times from multiple possible sources
  const amount = resolveBookingPrice(apiBooking as any);
  // Priority: stay.requestedCheckInTime/outTime -> root requested* -> metadata.checkInTime/outTime
  const requestedCheckInTime = bookingTime(apiBooking.agreedTimes, 'checkIn',
    apiBooking.stay?.requestedCheckInTime ||
    (apiBooking as any).requestedCheckInTime ||
    (apiBooking as any).metadata?.checkInTime ||
    undefined);
  const requestedCheckOutTime = bookingTime(apiBooking.agreedTimes, 'checkOut',
    apiBooking.stay?.requestedCheckOutTime ||
    (apiBooking as any).requestedCheckOutTime ||
    (apiBooking as any).metadata?.checkOutTime ||
    undefined);

  return {
    // ID field - Firestore document ID
    id: apiBooking.id,
    guestConfirmedAt: convertTimestampToString(apiBooking.guestConfirmedAt),
    
    // Basic fields
    bookingReference: apiBooking.bookingReference,
    source: mapSource(apiBooking.source),
    botBooking: (apiBooking as any).botBooking,
    canCreateInIloca: (apiBooking as any).canCreateInIloca,
    
    // Guest information (flatten) - CRITICAL FIX: Use optional chaining
    guestName: guestNameValue(apiBooking.guest?.fullName) || guestNameValue(apiBooking.guestName),
    guestEmail: apiBooking.guest?.email || apiBooking.guestEmail || undefined,
    guestPhone: apiBooking.guest?.primaryPhone || apiBooking.guestPhone || undefined,
    primaryPhone: apiBooking.guest?.primaryPhone || apiBooking.primaryPhone || undefined,
    email: apiBooking.guest?.email || apiBooking.email || undefined,
    numberOfGuests: apiBooking.counts?.totalGuests ?? apiBooking.numberOfGuests,
    
    // Room information (flatten) - CRITICAL FIX: Use optional chaining
    roomNumber: [apiBooking.room?.roomNumber, apiBooking.roomNumber].find(value => !isUnassignedRoom(value)) || '',
    roomLabel: bookingRoomLabel(apiBooking),
    roomName: (isBotSource(apiBooking.source) && apiBooking.botBooking?.enteredInIlocaAt == null
      ? [apiBooking.room?.roomName, apiBooking.roomName, apiBooking.room?.roomNumber, apiBooking.roomNumber]
      : [apiBooking.room?.roomNumber, apiBooking.roomName, apiBooking.roomNumber]).find(value => !isUnassignedRoom(value)) || '',
    roomType: apiBooking.room?.roomType || apiBooking.roomType || 'Standard',
    
    // Dates (flatten) - CRITICAL FIX: Handle both stay object and direct fields, with proper timestamp conversion
    checkIn: (() => {
      const stayCheckIn = apiBooking.stay?.checkIn;
      const directCheckIn = (apiBooking as any).checkIn;
      const timestamp = stayCheckIn || directCheckIn;
      if (!timestamp) return undefined;
      // If it's already a string (ISO format), use it directly
      if (typeof timestamp === 'string' && timestamp.match(/^\d{4}-\d{2}-\d{2}/)) {
        return timestamp;
      }
      // Otherwise, convert it
      return convertTimestampToString(timestamp) || undefined;
    })(),
    checkOut: (() => {
      const stayCheckOut = apiBooking.stay?.checkOut;
      const directCheckOut = (apiBooking as any).checkOut;
      const timestamp = stayCheckOut || directCheckOut;
      if (!timestamp) return undefined;
      // If it's already a string (ISO format), use it directly
      if (typeof timestamp === 'string' && timestamp.match(/^\d{4}-\d{2}-\d{2}/)) {
        return timestamp;
      }
      // Otherwise, convert it
      return convertTimestampToString(timestamp) || undefined;
    })(),
    
    // Requested times - use derived values (see above)
    agreedTimes: apiBooking.agreedTimes,
    requestedCheckInTime,
    arrival: apiBooking.agreedTimes?.arrival ?? null,
    // The model date is known but no approved time is in the plan yet (clarification or open decision).
    arrivalTimePending: !!apiBooking.agreedTimes?.arrival && (apiBooking.agreedTimes.arrival.needsClarification
      || !apiBooking.agreedTimes.checkIn || apiBooking.agreedTimes.checkIn.approved === false || apiBooking.agreedTimes.checkIn.planTime === null),
    requestedCheckOutTime,
    
    // Status
    status: mapStatus(apiBooking.status),
    bookingStatus: apiBooking.status as any,
    
    // Approval status fields
    earlyCheckinApproved: apiBooking.earlyCheckinApproved || (apiBooking as any).earlyCheckinApproved || false,
    lateCheckoutApproved: apiBooking.lateCheckoutApproved || (apiBooking as any).lateCheckoutApproved || false,
    invoiceSent: apiBooking.invoiceSent || (apiBooking as any).invoiceSent || false,
    
    // Additional data
      specialRequests: apiBooking.specialRequests || undefined,
      weitereAnfragen: (apiBooking as any).weitereAnfragen || undefined,
      rechnungAngefordert: (apiBooking as any).rechnungAngefordert || undefined,
      rechnungsadresse: (apiBooking as any).rechnungsadresse || undefined,
      zusaetzlicheEmails: (apiBooking as any).zusaetzlicheEmails || undefined,
    metadata: (apiBooking as any).metadata,
    blocked: (apiBooking as any).blocked,
    totalAmount: amount,
    priceReadOnly: isIlocaImported(apiBooking as any),
    totalGrossAmount: (apiBooking as any).financial?.totalGrossAmount ?? amount,
    currencyCode: apiBooking.currencyCode || (apiBooking as any).financial?.currency || 'EUR',
    financial: (apiBooking as any).financial || undefined,
    
    // References
    references: apiBooking.references || [],
    referenceMap: apiBooking.referenceMap || {},
    
    // Property/Accommodation (Unterkunft)
    propertyName: (apiBooking as any).metadata?.propertyName || (apiBooking as any).propertyName || undefined,
    propertyShortName: (apiBooking as any).metadata?.propertyShortName || (apiBooking as any).propertyShortName || undefined,
    
    // OTA Information (OTA Ref)
    otaReference: (apiBooking as any).otaReference || undefined,
    otaSourceName: (apiBooking as any).otaSourceName || undefined,
    otaName: (apiBooking as any).otaName || undefined,
    
    // Booking Date (Gebucht am)
    bookedDateTime: convertTimestampToString((apiBooking as any).metadata?.originalBookingDate)
      || convertTimestampToString((apiBooking as any).bookedDateTime)
      || undefined,

    // Stay details
    nights: (apiBooking as any).stay?.nights || undefined,

    // Counts
    numberOfAdults: (apiBooking as any).counts?.adults || undefined,
    numberOfChildren: (apiBooking as any).counts?.children || undefined,

    // ILOCA specific fields from metadata
    ilocaId: (apiBooking as any).ilocaId || (apiBooking as any).metadata?.illocaId || undefined,
    illocaInternalId: (apiBooking as any).metadata?.illocaInternalId || undefined,
    portalId: (apiBooking as any).portalId || (apiBooking as any).metadata?.portalId || undefined,
    pin: (apiBooking as any).metadata?.pin || undefined,
    checkInLink: (apiBooking as any).metadata?.checkInLink || undefined,
    accessMailLink: (apiBooking as any).metadata?.accessMailLink || undefined,
    checkInLinkPdf: (apiBooking as any).metadata?.checkInLinkPdf || undefined,
    roomMaxPersons: (apiBooking as any).metadata?.roomMaxPersons || undefined,
    roomCategory: (apiBooking as any).metadata?.roomCategory || undefined,
    roomId: (apiBooking as any).metadata?.roomId || undefined,
    wasCancelled: (apiBooking as any).metadata?.wasCancelled || false,
    wasModified: (apiBooking as any).metadata?.wasModified || false,
    lastUpdatedAt: convertTimestampToString((apiBooking as any).metadata?.lastUpdatedAt) || undefined,
    importedFrom: (apiBooking as any).metadata?.importedFrom || undefined,
    allGuests: (apiBooking as any).metadata?.allGuests || undefined,

    // Property address
    propertyAddress: (apiBooking as any).metadata?.propertyAddress || undefined,

    // Additional financial fields
    depositAmount: (apiBooking as any).financial?.depositAmount || undefined,
    paymentStatus: (apiBooking as any).financial?.paymentStatus || undefined,

    // Booking source (OTA origin)
    bookingSource: (apiBooking as any).metadata?.bookingSource || undefined,

    // Metadata - convert timestamps to strings (with fallback to empty string if undefined)
    createdAt: convertTimestampToString((apiBooking as any).createdAt || apiBooking.createdAt) || new Date().toISOString(),
    updatedAt: convertTimestampToString((apiBooking as any).updatedAt || apiBooking.updatedAt) || new Date().toISOString(),

    // Additional fields that might be in the raw data
    rawWebhookData: (apiBooking as any).rawWebhookData,
    webhookMetadata: (apiBooking as any).webhookMetadata,

    // DRE-657: Escalation approval data
    escalation: (apiBooking as any).escalation || undefined,
    aiRiskFactors: Array.isArray((apiBooking as any).metadata?.aiCategorization?.riskFactors)
      ? (apiBooking as any).metadata.aiCategorization.riskFactors : undefined,
    lastGuestMessageSource: (apiBooking as any).metadata?.lastGuestMessageSource || undefined,
    lastGuestMessageAt: convertTimestampToString((apiBooking as any).metadata?.lastGuestMessageUpdate),
    lastEmailAt: (apiBooking as any).metadata?.lastEmailId
      ? convertTimestampToString((apiBooking as any).metadata?.lastEmailUpdate) ?? '' : undefined,
  };
}

/**
 * Map API source to frontend source type
 */
function mapSource(apiSource: string): 'ILLOCA' | 'EVIIVO' | 'apaleo' | 'mews' | 'clock' | 'protel' | 'VOICE' | 'WHATSAPP' {
  // CRITICAL FIX: Add null/undefined check
  if (!apiSource || typeof apiSource !== 'string') {
    return 'ILLOCA'; // Default fallback - ILLOCA is our primary source
  }

  const sourceMap: Record<string, any> = {
    'VOICE': 'VOICE',
    'WHATSAPP': 'WHATSAPP',
    'ILLOCA': 'ILLOCA',
    'ILOCA': 'ILLOCA',  // Handle both spellings
    'EVIIVO': 'EVIIVO',
    'AIRBNB': 'ILLOCA', // Map OTA sources to ILLOCA (they come through Illoca)
    'BOOKING_COM': 'ILLOCA',
    'DIRECT': 'ILLOCA',
    'ZAPIER': 'ILLOCA',
    'apaleo': 'apaleo',
    'mews': 'mews',
    'clock': 'clock',
    'protel': 'protel'
  };

  return sourceMap[apiSource.toUpperCase()] || 'ILLOCA';
}

/**
 * Map API status to frontend status type
 */
function mapStatus(apiStatus: string): Booking['status'] {
  // CRITICAL FIX: Add null/undefined check
  if (!apiStatus || typeof apiStatus !== 'string') {
    return 'confirmed'; // Default fallback
  }
  
  const statusMap: Record<string, any> = {
    'ARCHIVED_ORPHAN': 'archived',
    'CONFIRMED': 'confirmed',
    'CANCELLED': 'cancelled',
    'MODIFIED': 'confirmed',
    'CHECKED_IN': 'checked_in',
    'CHECKED_OUT': 'checked_out',
    'TENTATIVE': 'tentative',
    'PENDING': 'tentative'
  };
  
  return statusMap[apiStatus.toUpperCase()] || 'confirmed';
}

/**
 * Calculate number of nights between check-in and check-out
 */
function calculateNights(checkIn: string, checkOut: string): number {
  const checkInDate = new Date(checkIn);
  const checkOutDate = new Date(checkOut);
  const diffTime = Math.abs(checkOutDate.getTime() - checkInDate.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays || 1;
}

/**
 * Transform API response to frontend format
 */
export function transformApiResponse(apiResponse: any): any {
  if (apiResponse.data && Array.isArray(apiResponse.data)) {
    // Transform array of bookings
    const transformed = apiResponse.data.map(transformApiToFrontend);
    
    return {
      ...apiResponse,
      data: transformed,
      bookings: transformed // Some components expect 'bookings' field
    };
  } else if (apiResponse.data && typeof apiResponse.data === 'object') {
    // Transform single booking
    const transformed = transformApiToFrontend(apiResponse.data);
    
    return {
      ...apiResponse,
      data: transformed,
      booking: transformed // Some components expect 'booking' field
    };
  }
  
  return apiResponse;
}

/**
 * Transform dashboard metrics response
 * CRITICAL FIX: Backend returns {arrivals, departures} but frontend expects {checkIns: {count, bookings}, checkOuts: {count, bookings}}
 */
export function transformDashboardMetrics(apiResponse: any): any {
  if (!apiResponse.data) return apiResponse;
  
  const metrics = apiResponse.data;
  
  // CRITICAL FIX: Always initialize checkIns and checkOuts to prevent "Cannot read properties of undefined" errors
  if (!metrics.checkIns) {
    metrics.checkIns = { count: 0, bookings: [] };
  }
  if (!metrics.checkOuts) {
    metrics.checkOuts = { count: 0, bookings: [] };
  }
  if (!metrics.staying) {
    metrics.staying = { count: 0, bookings: [] };
  }
  
  // CRITICAL FIX: Transform MVP Dashboard format (arrivals/departures) to classic format (checkIns/checkOuts)
  // Always transform arrivals/departures if they exist, even if checkIns/checkOuts already exist
  if (metrics.arrivals && Array.isArray(metrics.arrivals)) {
    // Transform arrivals to checkIns format
    const transformedArrivals = metrics.arrivals.map((arrival: any) => transformApiToFrontend({
      id: arrival.id,
      botBooking: arrival.botBooking,
      guestConfirmedAt: arrival.guestConfirmedAt,
      bookingReference: arrival.bookingReference,
      source: arrival.source || 'ILLOCA', // Use actual source or default to ILLOCA
      guest: {
        fullName: arrival.guestName
      },
      stay: {
        checkIn: arrival.checkIn,
        checkOut: arrival.checkIn, // Checkout not available for arrivals
        nights: 0
      },
      room: {
        roomNumber: arrival.roomNumber,
        roomName: arrival.roomName,
        roomType: 'Standard'
      },
      status: 'CONFIRMED',
      specialRequests: arrival.specialRequests || null
    }));
    
    // Merge with existing checkIns if present, otherwise replace
    metrics.checkIns = {
      count: metrics.arrivals.length,
      bookings: transformedArrivals
    };
  }
  
  if (metrics.departures && Array.isArray(metrics.departures)) {
    // Transform departures to checkOuts format
    const transformedDepartures = metrics.departures.map((departure: any) => {
      // CRITICAL FIX: Set checkIn to checkOut - 1 day, so it never matches checkIn filter
      let checkOutDate = departure.checkOut;
      if (typeof checkOutDate !== 'string') {
        if (checkOutDate?.toISOString) {
          checkOutDate = checkOutDate.toISOString();
        } else if (checkOutDate?._seconds) {
          checkOutDate = new Date(checkOutDate._seconds * 1000).toISOString();
        } else {
          checkOutDate = new Date(checkOutDate).toISOString();
        }
      }
      
      const checkOutDateObj = new Date(checkOutDate);
      checkOutDateObj.setDate(checkOutDateObj.getDate() - 1); // 1 Tag vorher
      const checkInDateStr = checkOutDateObj.toISOString();
      
      return transformApiToFrontend({
        id: departure.id,
        botBooking: departure.botBooking,
        guestConfirmedAt: departure.guestConfirmedAt,
        bookingReference: departure.bookingReference,
        source: departure.source || 'ILLOCA', // Use actual source or default to ILLOCA
        guest: {
          fullName: departure.guestName
        },
        stay: {
          checkIn: checkInDateStr, // 1 Tag vor checkOut - never matches checkIn filter
          checkOut: checkOutDate,
          nights: 0
        },
        room: {
          roomNumber: departure.roomNumber,
          roomName: departure.roomName,
          roomType: 'Standard'
        },
        status: 'CONFIRMED',
        specialRequests: departure.specialRequests || null
      });
    });
    
    // Merge with existing checkOuts if present, otherwise replace
    metrics.checkOuts = {
      count: metrics.departures.length,
      bookings: transformedDepartures
    };
  }
  
  // Ensure count is always set, even if bookings array is empty
  if (!metrics.checkIns.count && metrics.checkIns.bookings) {
    metrics.checkIns.count = metrics.checkIns.bookings.length;
  }
  if (!metrics.checkOuts.count && metrics.checkOuts.bookings) {
    metrics.checkOuts.count = metrics.checkOuts.bookings.length;
  }
  if (!metrics.staying.count && metrics.staying.bookings) {
    metrics.staying.count = metrics.staying.bookings.length;
  }
  
  // Transform bookings in check-ins, check-outs, and staying arrays (legacy format)
  if (metrics.checkIns?.bookings && Array.isArray(metrics.checkIns.bookings)) {
    metrics.checkIns.bookings = metrics.checkIns.bookings.map(transformApiToFrontend);
  }
  if (metrics.checkOuts?.bookings && Array.isArray(metrics.checkOuts.bookings)) {
    metrics.checkOuts.bookings = metrics.checkOuts.bookings.map(transformApiToFrontend);
  }
  if (metrics.staying?.bookings && Array.isArray(metrics.staying.bookings)) {
    metrics.staying.bookings = metrics.staying.bookings.map(transformApiToFrontend);
  }
  
  return apiResponse;
}
/** PO 2026-10-10: operational lists omit archived bookings; search retains them. */
export function isActiveBooking(booking: { status?: unknown; bookingStatus?: unknown }): boolean {
  return ![booking.status, booking.bookingStatus].some(status =>
    typeof status === 'string' && ['ARCHIVED', 'ARCHIVED_ORPHAN'].includes(status.toUpperCase()));
}

/** Cancelled bookings (status CANCELLED) are not operational stays; the list shows them only on request. */
export function isCancelledBooking(booking: { status?: unknown; bookingStatus?: unknown }): boolean {
  return [booking.status, booking.bookingStatus].some(status => typeof status === 'string' && status.toUpperCase() === 'CANCELLED');
}
